import React, { useRef, useEffect, useState } from "react";

/**
 * Reusable Documentation Section Component
 */
const DocSection = ({ id, title, emoji, children, darkMode = false, isCollapsed = true, onToggle }) => {
  const theme = darkMode
    ? {
        background: "#1e293b",
        border: "#334155",
        text: "#e2e8f0",
        headerBg: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      }
    : {
        background: "#ffffff",
        border: "#e2e8f0",
        text: "#1e293b",
        headerBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      };

  return (
    <div
      id={id}
      style={{
        maxWidth: "1200px",
        margin: "80px auto",
        padding: "0 20px",
        scrollMarginTop: "100px", // Offset for fixed header
        minWidth: 0,
        width: "100%",
      }}
      className="doc-section"
    >
      <div
        style={{
          backgroundColor: theme.background,
          borderRadius: "16px",
          boxShadow: darkMode
            ? "0 8px 32px rgba(0, 0, 0, 0.3)"
            : "0 4px 20px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.border}`,
          overflow: "hidden",
        }}
      >
        {/* Section Header */}
        <div
          onClick={onToggle}
          role={onToggle ? "button" : undefined}
          aria-expanded={!isCollapsed}
          style={{
            background: theme.headerBg,
            padding: "32px",
            color: "white",
            cursor: onToggle ? "pointer" : "default",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
              fontWeight: "700",
              margin: "0",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {emoji && <span style={{ fontSize: "2rem" }}>{emoji}</span>}
            <span>{title}</span>
          </h2>
          {onToggle && (
            <div
              aria-hidden
              className={`doc-section-toggle ${isCollapsed ? "collapsed" : "expanded"}`}
              style={{
                fontSize: "2.4rem",
                fontWeight: "600",
                lineHeight: "1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.28s ease",
                userSelect: "none",
                width: 52,
                height: 52,
                borderRadius: 52,
                background: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
              }}
            >
              <span style={{ transition: "transform 0.28s ease" }}>{isCollapsed ? "+" : "−"}</span>
            </div>
          )}
        </div>

        {/* Section Content */}
        <div
          className={`doc-section-content ${isCollapsed ? "collapsed" : "expanded"}`}
          style={{
            padding: isCollapsed ? "0 32px" : "40px 32px",
            color: theme.text,
            overflow: "hidden",
            transition: "max-height 0.36s cubic-bezier(.2,.9,.2,1), opacity 0.25s ease, transform 0.28s ease",
            textAlign: "left",
            minWidth: 0,
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        >
          <div className="doc-section-inner-content">
            {children}
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {!isCollapsed && (
        <a
          href="#top"
          style={{
            display: "inline-block",
            marginTop: "24px",
            padding: "12px 24px",
            backgroundColor: darkMode ? "#3b82f6" : "#667eea",
            color: "white",
            borderRadius: "8px",
            textDecoration: "none",
            fontWeight: "600",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          ↑ Back to Top
        </a>
      )}
    </div>
  );
};

export default DocSection;
